import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TestConnection, TestConnectionResponse } from '../models/query/test-connection.model';
import { EntityColumnHeaders } from '../models/query/entity-column-headers.model';
import { environment } from 'src/environments/environment';
import { ColumnHeader } from '../models/query/column-header.model';
import { QueryResult } from '../models/query/query-result.model';
import { QueryPagedRequest } from '../requests/query-paged.request';
import { ExplainPlan } from '../models/query/explain-plan.model';
import { QueryRequest } from '../requests/query.request';
import { Column } from '../components/report/models/column.model';
import { ReportEntity } from '../components/report/models/report-entity.model';
import { Report } from '../components/report/models/report.model';
import { Query } from '../models/query/query.model';
import { ProviderTypeEnum } from '../enums/provider-type.enum';
import { ObjectValue } from '../models/object-value.model';
import { EntityList } from 'src/app/models/entityList';
import { map, timeout } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class QueryService {

  //#region Properties

  entityColumnHeaders: Array<EntityColumnHeaders> = new Array<EntityColumnHeaders>();

  afterStoreColumnHeaders = new EventEmitter<EntityColumnHeaders>();

  //#endregion

  constructor(private http: HttpClient) { }

  getQuery(obj: Report) {
    return this.http.post<Query>(environment.apiUrl + 'api/report/query', obj);
  }

  getQueryDetail(obj: Report) {
    return this.http.post<Query>(environment.apiUrl + 'api/report/query/detail', obj);
  }

  columnHeader(obj: QueryRequest) {
    return this.http.post<ColumnHeader[]>(environment.apiUrl + 'api/report/query/columnheaders', obj);
  }

  explainPlan(obj: QueryRequest) {
    return this.http.post<ExplainPlan>(environment.apiUrl + 'api/report/query/explainplan', obj);
  }

  queryResult(obj: QueryPagedRequest) {
    return this.http.post<QueryResult>(environment.apiUrl + 'api/report/query/queryresults', obj);
  }

  queryResultWithTimeout(obj: QueryPagedRequest) {
    return this.http.post<QueryResult>(environment.apiUrl + 'api/report/query/queryresults', obj)
      .pipe(timeout(environment.reportViewTimeout == null ? 55000 : environment.reportViewTimeout));
  }

  queryEntityList(obj: QueryRequest) {
    return this.http.post<EntityList[]>(environment.apiUrl + 'api/report/query/entitylist', obj);
  }

  //#region Entity Columns

  storeEntityColumnHeader(id: number, columns: Array<ColumnHeader>) {
    let columnHeader = this.getEntityColumnHeader(id);

    if (columnHeader == null) {
      columnHeader = {
        Id: id,
        Columns: columns
      };

      this.entityColumnHeaders.push(columnHeader);
    }

    this.afterStoreColumnHeaders.emit(columnHeader);
  }

  getEntityColumnHeader(id: number): EntityColumnHeaders {
    const find = this.entityColumnHeaders.find(value => {
      return value.Id === id;
    });

    return find;
  }

  clearEntityColumnHeader(): void {
    this.entityColumnHeaders = new Array<EntityColumnHeaders>();
  }

  converColumnHeadersToColumns(headers: Array<ColumnHeader>, entity: ReportEntity): Array<Column> {
    const columns: Array<Column> = [];
    if (entity) {
      headers.forEach(value => {
        columns.push(this.converColumnHeaderToColumn(value, entity));
      });
    }
    return columns;
  }

  private converColumnHeaderToColumn(header: ColumnHeader, entity: ReportEntity): Column {
    return {
      ReportEntityId: entity.Id,
      EntityId: entity.Entity.Id,
      Name: header.Name,
      Type: header.Type,
      IsPrimaryKey: header.IsPrimaryKey,
      Alias : '',
      Order : null,
      IsGroupBy: false,
      SystemType : header.SystemType,
      TotalizerType: null
    };
  }

  //#endregion

  testConnection(obj: TestConnection) {
    return this.http.post<TestConnectionResponse>(environment.apiUrl + 'api/report/query/testConnection', obj);
  }

  getProvidersOptions(): ObjectValue[] {
    return [
      new ObjectValue('Oracle', ProviderTypeEnum.Oracle),
      new ObjectValue('PostgreSQL', ProviderTypeEnum.PostgreSQL)
    ];
  }
}
